// Opportunities.js
import React, { useState, useEffect } from "react";
import styles from "./DashboardOpportunities.module.css";
import buttonStyle from "../../../GlobalStyles/button.module.css";
import {
  Card, 
  CardContent,
  Grid,
  Paper,
  CircularProgress,
  Typography,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import jsonData from "./data.json";
import UploadModal from "../../../Components/UploadPopUp/UploadPopUp";
import TaskCard from '../../../Components/TaskCard/TaskCard';
import { useMsal } from "@azure/msal-react";

import { useDispatch, useSelector } from "../../../redux/store";
import {
  getTasks,
  getAssignees,
  startLoading,
  uploadDoc,
  uploadDocsBatch,
  clearProfilePictureMemoryCache,
} from "../../../redux/slices/dashboard";
import ApiResponseDisplay from "../../../Components/ApiResponseDisplay/ApiResponseDisplay";
import { slice } from '../../../redux/slices/dashboard';

const Item = styled(Paper)(({ theme }) => ({
  backgroundColor: theme.palette.mode === "dark" ? "#1A2027" : "#fff",
  ...theme.typography.body2,
  padding: theme.spacing(1),
  textAlign: "center",
  color: theme.palette.text.secondary,
}));

function DashboardOpportunities() {
  const dispatch = useDispatch();
  const { accounts } = useMsal();
  const account = accounts[0];

  const [showUploadModal, setshowUploadModal] = useState(false);
  const [selectedTask, setSelectedTask] = useState({});
  const [comment, setComment] = useState("");
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [isResponseShown, setIsResponseShown] = useState(false);
  const [selectedFileDetails, setSelectedFileDetails] = useState({});
  const { tasks, isAssigneeLoading, isLoading, error } = useSelector((state) => state.dashboard);
  const [uploadList, setUploadList] = useState([]);
  const [feePropNonBillPayerList, setFeePropNonBillPayerList] = useState([]);
  const [feePropBillPayerList, setFeePropBillPayerList] = useState([]);
  const [isaToDoList, setIsaToDoList] = useState([]);
  const [isaToSendList, setIsaToSendList] = useState([]);
  const [fbpsList, setFbpsList] = useState([]);
  const [useBatchUpload, setUseBatchUpload] = useState(true);
  const [isUploading, setIsUploading] = useState(false);

  useEffect(() => {
    dispatch(getTasks());
    dispatch(getAssignees());

  }, []);

  // Cleanup profile picture cache on unmount to prevent memory leaks
  useEffect(() => {
    return () => {
      clearProfilePictureMemoryCache();
    };
  }, []);

  useEffect(() => {

    if (error && error?.response?.data !== null) {
      setIsResponseShown(true);
    }

  }, [error]);

  // Helper function to parse date strings (dd/mm/yyyy)
  const parseDate = (dateString) => {
    if (!dateString) return new Date(0); // Treat missing dates as very old
    const parts = dateString.split('/');
    // Note: months are 0-indexed in JavaScript Date objects (0 for January, 11 for December)
    return new Date(parts[2], parts[1] - 1, parts[0]);
  };

  useEffect(() => {
    const uploadsdata = tasks.filter((task) => task.deadlineType == "upload");
    const feepropdata = tasks.filter((task) => task.deadlineType == "fee_proposal");
    const feepropNonBPdata = feepropdata.filter(task => !(task.clientName && task.clientName.includes('Bill Payer')));
    const feepropBPdata = feepropdata.filter(task => task.clientName && task.clientName.includes('Bill Payer'));
    const isatododata = tasks.filter((task) => task.deadlineType == "isa_todo");
    const isatosenddata = tasks.filter((task) => task.deadlineType == "isa_to_sent");
    const fbpsdata = tasks.filter((task) => task.deadlineType == "fbps");

    // Sort each list by deadlineDate (oldest to newest)
    const sortByDeadline = (a, b) => parseDate(a.deadlineDate) - parseDate(b.deadlineDate);

    setUploadList(uploadsdata.sort(sortByDeadline));
    setFeePropNonBillPayerList(feepropNonBPdata.sort(sortByDeadline));
    setFeePropBillPayerList(feepropBPdata.sort(sortByDeadline));
    setIsaToDoList(isatododata.sort(sortByDeadline));
    setIsaToSendList(isatosenddata.sort(sortByDeadline));
    setFbpsList(fbpsdata.sort(sortByDeadline));
  }, [tasks]);

  const setupUploadModal = (task, fileDetails) => {
    setSelectedTask(task);
    setSelectedFileDetails(fileDetails); // Save the details of the selected file
    setshowUploadModal(true);
  };

  const closeModal = () => {
    setSelectedTask({});
    setshowUploadModal(false);
    setIsUploading(false); // Reset uploading state when modal closes
    setComment("");
    setUploadedFiles([]);
  };

  const handleUpload = (files) => {
    // Here, you might want to update the state or perform some action with the files
    setUploadedFiles(files);
    files.forEach((file) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        console.log(`File name: ${file.name}`);
        console.log(`Content bytes:`, e.target.result);
      };
      reader.readAsArrayBuffer(file);
    });
  };

  const handleSubmit = async (shouldMergePDFs = false) => {
    console.log("Submitted Files: ", uploadedFiles);
    console.log("Comment: ", comment);
    console.log("task: ", selectedTask);
    console.log("Should merge PDFs: ", shouldMergePDFs);
    
    // Set uploading state for modal feedback
    setIsUploading(true);
    dispatch(startLoading());

    try {
      if (uploadedFiles.length > 1) {
        // Use batch upload for multiple files (with or without PDF merging)
        await dispatch(uploadDocsBatch(uploadedFiles, selectedTask.projectNumber, selectedTask.address, selectedTask.deadlineID,
          selectedTask.deadlineType, selectedFileDetails.uploadID, selectedFileDetails.uploadType, account.username, comment, shouldMergePDFs));
        console.log(`Batch upload successful (PDF merging: ${shouldMergePDFs ? 'ON' : 'OFF'})`);
      } else if (uploadedFiles.length === 1) {
        // Use individual upload for single file (more efficient)
        const file = uploadedFiles[0];
        console.log("Uploading single file:", file.name);
        await dispatch(uploadDoc(file, selectedTask.projectNumber, selectedTask.address, selectedTask.deadlineID,
          selectedTask.deadlineType, selectedFileDetails.uploadID, selectedFileDetails.uploadType, account.username, comment));
        console.log("Single file upload completed");
      }

      // Upload successful - close modal and reset state
      closeModal();
      setComment("");
      setUploadedFiles([]);
      
    } catch (error) {
      console.error("Upload failed:", error);
      // On error, keep modal open so user can retry
    } finally {
      // Always stop the uploading state
      setIsUploading(false);
    }
  };

  // Initial
  return (
    <div className={styles.opportunitiesContainer}>
      <div className={styles.pageTitle}>Opportunities</div>

      {isLoading && isAssigneeLoading && (
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', padding: '24px' }}>
          <CircularProgress size={40} />
        </div>
      )}

      {!isLoading && !isAssigneeLoading && (
        <Grid
          container
          wrap="nowrap"
          sx={{ flexDirection: { xs: "column", md: "row" }, mt: 5 }}
          spacing={2}
        >
          <Grid item xs={3}>
            <div className={styles.oppHead}>Uploads</div>
            <div className={styles.scrollableColumn}>
              {
              uploadList.length > 0 ?
              uploadList?.map((task) => (
                <TaskCard
                  key={task.id}
                  task={task}
                  setupUploadModal={setupUploadModal}
                  buttonStyle={buttonStyle}
                />
              ))
              : <Card className={styles.Card}>
                  <CardContent className={styles.CardContent}>
                    <Typography
                          gutterBottom
                          variant="h5"
                          component="div"
                          className={styles.cardTitle}
                          style={{ flex: 1, wordWrap: "break-word" }}
                      >
                          No Uploads
                      </Typography>
                  </CardContent>
                </Card>
                }
            </div>
          </Grid>
          <Grid item xs={3}>
            <div className={styles.oppHead}>Fee Proposals | Non-BP</div>
            <div className={styles.scrollableColumn}>
              {
              feePropNonBillPayerList.length > 0 ?
              feePropNonBillPayerList?.map((task) => (
                <TaskCard
                  key={task.id}
                  task={task}
                  setupUploadModal={setupUploadModal}
                  buttonStyle={buttonStyle}
                />
              ))
              : <Card className={styles.Card}>
                  <CardContent className={styles.CardContent}>
                    <Typography
                          gutterBottom
                          variant="h5"
                          component="div"
                          className={styles.cardTitle}
                          style={{ flex: 1, wordWrap: "break-word" }}
                      >
                          No Non-BP Fee Proposals
                      </Typography>
                  </CardContent>
                </Card>
              }
            </div>
          </Grid>
          <Grid item xs={3}>
            <div className={styles.oppHead}>Fee Proposals | Bill Payer</div>
            <div className={styles.scrollableColumn}>
              {
              feePropBillPayerList.length > 0 ?
              feePropBillPayerList?.map((task) => (
                <TaskCard
                  key={task.id}
                  task={task}
                  setupUploadModal={setupUploadModal}
                  buttonStyle={buttonStyle}
                />
              ))
              : <Card className={styles.Card}>
                  <CardContent className={styles.CardContent}>
                    <Typography
                          gutterBottom
                          variant="h5"
                          component="div"
                          className={styles.cardTitle}
                          style={{ flex: 1, wordWrap: "break-word" }}
                      >
                          No Bill Payer Fee Proposals
                      </Typography>
                  </CardContent>
                </Card>
              }
            </div>
          </Grid>
          {/* --- ISA Change: Comment out the following Grid item to hide 'ISAs | To Do' column --- */}
          {/*
          <Grid item xs={12 / 5}>
            <div className={styles.oppHead}>ISAs | To Do</div>
            <div className={styles.scrollableColumn}>
              {
              isaToDoList.length > 0 ?
              isaToDoList?.map((task) => (
                <TaskCard
                  key={task.id}
                  task={task}
                  setupUploadModal={setupUploadModal}
                  buttonStyle={buttonStyle}
                />
              ))
              
              : <Card className={styles.Card}>
                  <CardContent className={styles.CardContent}>
                    <Typography
                          gutterBottom
                          variant="h5"
                          component="div"
                          className={styles.cardTitle}
                          style={{ flex: 1, wordWrap: "break-word" }}
                      >
                          No Outstanding ISAs ToDo
                      </Typography>
                  </CardContent>
                </Card>
                }
            </div>
          </Grid>
          */}
          {/* --- To restore 'ISAs | To Do' column: uncomment the Grid item above --- */}

          {/* --- ISA Change: Comment out the following Grid item to hide 'ISAs | To Send' column --- */}
          {/*
          <Grid item xs={12 / 5}>
            <div className={styles.oppHead}> ISAs | To Send</div>
            <div className={styles.scrollableColumn}>
              {
              isaToSendList.length > 0 ?
              isaToSendList?.map((task) => (
                <TaskCard
                  key={task.id}
                  task={task}
                  setupUploadModal={setupUploadModal}
                  buttonStyle={buttonStyle}
                />
              )) 
              : <Card className={styles.Card}>
                  <CardContent className={styles.CardContent}>
                    <Typography
                          gutterBottom
                          variant="h5"
                          component="div"
                          className={styles.cardTitle}
                          style={{ flex: 1, wordWrap: "break-word" }}
                      >
                          No ISAs To Be Sent
                      </Typography>
                  </CardContent>
                </Card>}
            </div>
          </Grid>
          */}
          {/* --- To restore 'ISAs | To Send' column: uncomment the Grid item above --- */}

          <Grid item xs={3}>
            <div className={styles.oppHead}>FBPs</div>
            <div className={styles.scrollableColumn}>
              {
              fbpsList.length > 0 ? 
              fbpsList?.map((task) => (
                <TaskCard
                  key={task.id}
                  task={task}
                  setupUploadModal={setupUploadModal}
                  buttonStyle={buttonStyle}
                />
              ))
              : <Card className={styles.Card}>
                  <CardContent className={styles.CardContent}>
                    <Typography
                          gutterBottom
                          variant="h5"
                          component="div"
                          className={styles.cardTitle}
                          style={{ flex: 1, wordWrap: "break-word" }}
                      >
                          No Outstanding FBPs
                      </Typography>
                  </CardContent>
                </Card>
                }
            </div>
          </Grid>
        </Grid>
      )}
      <UploadModal
        show={showUploadModal}
        onClose={() => closeModal()}
        onUpload={handleUpload}
        onCommentChange={setComment}
        onSubmit={handleSubmit}
        comment={comment}
        enableBatchUpload={useBatchUpload}
        onBatchUploadChange={setUseBatchUpload}
        isUploading={isUploading}
      />
      {isResponseShown && (
        <ApiResponseDisplay
          apiResponse={error?.response?.data}
          setIsResponseShown={setIsResponseShown}
          onClose={() => dispatch(slice.actions.hasError(null))}
        />
      )}
    </div>
  );
}

export default DashboardOpportunities;
